import { OrderEntity } from '@genial/services';
import { ResponsesOrderResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { capitalize } from 'lodash';

import { getValidatedStatusIcon } from '../../../../utils/getValidatedStatusIcon';
import { BasketAdvisorListDataItem } from '../../../BatchTicket/utils/types';
import { formatAvailableAmount } from '../../../NewApplication/Product/components/FixedIncome/constants';
import {
  checkOrderIsDirectTreasury,
  checkOrderIsEquity,
  checkOrderIsFixedIncomeProduct,
  checkOrderIsFundProduct,
  checkOrderIsStructuredProduct,
} from '../constants';
import { EquityExecutionActions } from '../EquityExecutionActions';
import { OrderFullStatus } from '../OrderFullStatus';

export interface OrderEntityWithEvents extends OrderEntity {
  orderEvents?: ResponsesOrderResponse['orderEvents'];
  requestUserFullName?: ResponsesOrderResponse['requestUserFullName'];
  viewportType?: 'mobile' | 'tablet' | 'desktop';
}

export interface OrderDetailsProps {
  item: OrderEntityWithEvents;
  handleCloseDetails(): void;
}

export interface OrderDetailsTopProps {
  item: OrderEntityWithEvents;
  onClose(): void;
}

const messageError = '--';

export const getNotificationChannels = (
  item: OrderEntityWithEvents | BasketAdvisorListDataItem
) => {
  const notificationChannels =
    item?.notification?.notificationChannels?.map((channel) => capitalize(channel)).join(' - ') ||
    messageError;

  return notificationChannels;
};

export const getDetailsItems = (
  item: OrderEntityWithEvents,
  b2bShowOrderHistoryEquityActions: boolean
) => {
  const productTypeDetail = {
    label: 'Produto',
    value: item.marketType || messageError,
  };

  const assetDetail = {
    label: 'Ativo',
    value: item.offer.productDisplay || messageError,
  };

  const requestedAmountDetail = {
    label: 'Valor enviado',
    value: Utils.Mask.Money(item.requestedAmount),
  };

  const sideIsSell = item?.side === 'V';

  const statusDetail = {
    label: 'Status',
    value: (
      <OrderFullStatus
        statusIcon={getValidatedStatusIcon(item.orderStatus.statusIcon)}
        statusMessage={item.orderStatus.status}
      />
    ),
  };

  const reasonDetail = {
    label: 'Motivo',
    value: item?.error?.internalMessage || item?.error?.externalMessage || messageError,
  };

  if (checkOrderIsEquity(item)) {
    const orderEquityItems = [
      productTypeDetail,
      assetDetail,
      { label: 'Operação', value: item.sideDescription || messageError },
      { label: 'Tipo da ordem', value: item.orderType || messageError },
      { label: 'Preço médio executado', value: Utils.Mask.Money(item.executedAveragePrice) },
      { label: 'Qtd. solicitada', value: `${item.requestedQuantity}` },
      { label: 'Qtd. executada', value: `${item.executedQuantity}` },
      { label: 'Qtd. em aberto', value: `${item.openedQuantity}` },
      { label: 'Qtd. total cancelada', value: `${item.canceledQuantity}` },
      statusDetail,
      reasonDetail,
    ];

    if (b2bShowOrderHistoryEquityActions) {
      const positionExecutionActions = {
        mobile: orderEquityItems.length,
        tablet: 3,
        desktop: 5,
      };

      orderEquityItems.splice(positionExecutionActions[item?.viewportType || 'desktop'], 0, {
        label: '',
        value: <EquityExecutionActions data={item} />,
      });
    }

    return orderEquityItems;
  }

  if (checkOrderIsStructuredProduct(item)) {
    return [
      { label: 'Operações', value: item.offer.productDisplay || messageError },
      { label: 'Tipo de boletagem', value: item.billingType?.billingDescription || messageError },
      requestedAmountDetail,
      statusDetail,
      reasonDetail,
    ];
  }

  if (checkOrderIsDirectTreasury(item)) {
    const quantitySellFormatted = `${String(item?.quantity ?? messageError).replace(
      '.',
      ','
    )} unidades`;
    const quantityBuyFormatted = `${item.quantity?.toFixed(2).replace('.', ',')}` || messageError;
    const quantityFormatted = sideIsSell ? quantitySellFormatted : quantityBuyFormatted;

    const quantityDetail = {
      label: sideIsSell ? 'Unidades' : 'Quantidade',
      value: quantityFormatted,
    };

    const amountDetail = {
      label: sideIsSell ? 'Valor bruto solicitado' : requestedAmountDetail.label,
      value: requestedAmountDetail.value,
    };

    return [assetDetail, quantityDetail, amountDetail, statusDetail, reasonDetail];
  }

  if (checkOrderIsFundProduct(item) && sideIsSell) {
    const executedAmountFormatted = Utils.Mask.Money(item.executedAmount || 0);
    const quotationDate =
      item.offer.requestedDateQuotation?.quotationDate || item.offer.quotationDate;
    const quotationDateFormatted = quotationDate
      ? Utils.Mask.DateFormat(quotationDate, 'dd/MM/yyyy')
      : messageError;
    return [
      assetDetail,
      { label: 'Data de cotização', value: quotationDateFormatted },
      { label: 'Valor líquido estimado', value: executedAmountFormatted },
      statusDetail,
      reasonDetail,
    ];
  }

  if (checkOrderIsFixedIncomeProduct(item) && sideIsSell) {
    return [
      assetDetail,
      { label: 'Qtd. solicitada', value: formatAvailableAmount(item?.quantity) },
      { label: 'Valor líquido', value: Utils.Mask.Money(item?.amount) },
      statusDetail,
      reasonDetail,
    ];
  }

  if (checkOrderIsFixedIncomeProduct(item)) {
    const hasFeeDetail = !!item.requestedOfferDetail?.customerFeeAmount;

    const customerFeeAmount = hasFeeDetail
      ? Utils.Mask.Percentage(item.requestedOfferDetail?.customerFeeAmount)
      : Utils.Mask.Percentage(item.offer.profitabilityTax);
    const customerPUAmount = hasFeeDetail
      ? Utils.Mask.Money(item.requestedOfferDetail?.customerPuAmount, {
          maximumFractionDigits: 8,
        })
      : Utils.Mask.Money(item.offer.unitPrice, {
          maximumFractionDigits: 8,
        });

    return [
      assetDetail,
      { label: 'Taxa Cliente', value: customerFeeAmount || messageError },
      { label: 'PU Cliente', value: customerPUAmount || messageError },
      requestedAmountDetail,
      statusDetail,
      reasonDetail,
    ];
  }

  return [assetDetail, requestedAmountDetail, statusDetail, reasonDetail];
};

export const getPreDetailsItems = (item: OrderEntityWithEvents) => {
  if (checkOrderIsEquity(item) || checkOrderIsStructuredProduct(item)) {
    return [
      {
        label: 'Solicitação foi gravada?',
        value: item.interactionChannelAuditDetail.description || messageError,
      },
      {
        label: 'Canal de solicitação',
        value: item.interactionChannelAuditDetail.interactionChannelOption.channel || messageError,
      },
    ];
  }

  return [];
};

export const getExtraDetailsItems = (
  item?: OrderEntityWithEvents,
  b2bShowOrderHistoryEquityActions?: boolean
) => {
  if (!item) {
    return [];
  }
  if (checkOrderIsEquity(item)) {
    return (item.orderEvents || []).map((event) => {
      const orderEventItem = [
        {
          label: 'Qtd. aberta',
          value: `${event.openedQuantity}`,
        },
        {
          label: 'Qtd. executada',
          value: `${event.executedQuantity}`,
        },
        {
          label: 'Preço médio exe.',
          value: Utils.Mask.Money(event.executedAveragePrice),
        },
        {
          label: 'Últ. atualização',
          value: Utils.Mask.DateFormat(event.orderEventDate || undefined, 'dd/MM/yyyy HH:mm:ss'),
        },
        {
          label: 'Status',
          value: (
            <OrderFullStatus
              statusIcon={getValidatedStatusIcon(event.eventStatus.statusIcon)}
              statusMessage={event.eventStatus.status}
            />
          ),
        },
      ];

      if (b2bShowOrderHistoryEquityActions) {
        orderEventItem.splice(2, 0, {
          label: 'Qtd. cancelada',
          value: `${event.canceledQuantity}`,
        });
      }

      return orderEventItem;
    });
  }

  return [];
};

export const getAssetsColumns = (item: OrderEntityWithEvents) => {
  const sideIsSell = item?.side === 'V';
  if (checkOrderIsEquity(item)) {
    const equityColumnsCount = {
      mobile: 1,
      tablet: 4,
      desktop: 6,
    };

    return equityColumnsCount[item?.viewportType || 'desktop'];
  }
  if (checkOrderIsFixedIncomeProduct(item) && !sideIsSell) {
    const fixedIncomeColumnsCount = {
      mobile: 1,
      tablet: 4,
      desktop: 6,
    };

    return fixedIncomeColumnsCount[item?.viewportType || 'desktop'];
  }
};
